import { useMemo } from 'react';

import { AspectRatio, Group, Overlay, Stack, Text } from '@mantine/core';

import { Badge, Card, CardProps, ImageMantine } from '../../../../DataDisplay';
import classes from './ProductCard.module.css';

interface ProductCardBadgesProps {
  priceBadge?: string;
  soldOut?: boolean;
  tag?: string;
}

const ProductCardBadges = ({ priceBadge, soldOut, tag }: ProductCardBadgesProps) => {
  return (
    <Group gap="xs" pos="absolute" left={8} top={8} style={{ zIndex: 2 }}>
      {priceBadge && (
        <Badge type="white" variant="filled">
          {priceBadge}
        </Badge>
      )}
      {soldOut && (
        <Badge type="error" variant="filled">
          Sold Out
        </Badge>
      )}
      {tag && (
        <Badge type="success" variant="filled">
          {tag}
        </Badge>
      )}
    </Group>
  );
};

interface ProductCardBaseProps extends CardProps {
  banner?: boolean; // This boolean simply determine the aspect ratio of the card image
  img?: string;
  thumbnail?: string;
  title?: string;
  description?: string;
  price?: string;
  sale?: string;
  tag?: string;
  priceBadge?: string; // Optional price badge to display in the top-right corner
  soldOut?: boolean;
  isOutOfStock?: boolean;
  onClick?: () => void;

  /**
   * Determines how to display the cards border state when clicked
   * @default 'hover'
   */
  clickDisplay?: 'active' | 'hover';
}

interface ProductCardWithThumbnailProps extends ProductCardBaseProps {
  thumbnail?: string;
  tag?: never;
}

interface ProductCardWithTagProps extends ProductCardBaseProps {
  tag?: string;
  thumbnail?: never;
}

type ProductCardProps = ProductCardWithThumbnailProps | ProductCardWithTagProps;

const ProductCard = ({
  banner,
  img,
  thumbnail,
  title,
  description,
  price,
  sale,
  tag,
  priceBadge,
  soldOut,
  isOutOfStock,
  clickDisplay = 'hover',
  ...props
}: ProductCardProps) => {
  const renderPrice = useMemo(() => {
    if (isOutOfStock) {
      return (
        <Group>
          <Text fz="sm" fw={600} c="cerise">
            Sold Out
          </Text>
        </Group>
      );
    }

    if (price) {
      return (
        <Group gap="xs">
          {sale && (
            <Text fz="sm" c="gray" td="line-through">
              {sale}
            </Text>
          )}
          <Text fz="sm" fw={600} c="mint">
            {price}
          </Text>
        </Group>
      );
    }
    return null;
  }, [price, sale, isOutOfStock]);

  return (
    <Card
      component="button"
      className={classes.card}
      mod={{ 'click-display': clickDisplay }}
      {...props}
    >
      <Stack gap={0} w="100%">
        <AspectRatio ratio={banner ? 2 / 1 : 3 / 2}>
          <ImageMantine src={img} radius="md" className={classes.banner_img} />
          {soldOut && <Overlay radius="md" color="rgba(229, 82, 121, 0.5)" zIndex={1} />}
        </AspectRatio>
        <ProductCardBadges priceBadge={priceBadge} soldOut={soldOut} tag={tag} />

        {thumbnail && (
          <ImageMantine
            src={thumbnail}
            radius={12}
            className={classes.thumbnail}
            w={36}
            h={36}
            pos="absolute"
            left={8}
            top={8}
          />
        )}
        <Stack p="sm" gap={4}>
          <Text fz={'sm'} fw={600} lineClamp={2} ta="left">
            {title}
          </Text>
          {description && (
            <Text fz={11} c="gray" lineClamp={2} ta="left">
              {description}
            </Text>
          )}
          {renderPrice}
        </Stack>
      </Stack>
    </Card>
  );
};
export { ProductCard };
